import React, { useRef, useState } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { beginForgottenPasswordProcess, resendVerificationCode, resetForgottenPassword } from '../../../clients/profile/ProfileClient';
import Colors from '../../../constants/Colors';
import { isValidEmail } from '../../../utilities/Validation';
import {showNotificationBar} from "../../../actions/error-pages/NotificationBarActions";
import {StyledTextField} from "../../common/StyledTextField";
import {LoadingButton, TextButton} from "../../common/StyledButtons";
import { DesktopBox, MobileBox } from "../../common/Boxes";

const ForgotPasswordForm = ({ location, history }) => {

  const [hasResentCode, setHasRecentCode] = useState();
  const [hasSentCode, setHasSentCode] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState();
  const [newPassword, setNewPassword] = useState();
  const [confirmNewPassword, setConfirmNewPassword] = useState();
  const [isLoading, setIsLoading] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [errors, setErrors] = useState([]);

  const userRef = useRef(location.state ? location.state.user : undefined);

  const goToSignIn = () => {
    history.replace('/login')
  }

  const handleResendCode = async event => {
    try {
      if (userRef.current.email === undefined) history.push('/login');
      await resendVerificationCode(userRef.current.email);
      setHasRecentCode(true);
      showNotificationBar('info', 'Verification code sent.');
    } catch(err) {
      console.error('resend code error', err)
      showNotificationBar('error', 'An issue happened when sending the verification code.');
    }
  }

const isFormValid = () => isValidEmail(email)

const isPasswordFormValid = () => {
  const foundErrors = [];
  const capitalVal = (/[A-Z]/.test(newPassword));
  const numberVal = (/\d/.test(newPassword));
  if (!code || code.length !== 6) foundErrors.push('verificationCode');
  if (!newPassword || newPassword.length < 8 || !capitalVal || !numberVal) foundErrors.push('newPassword');
  if (!foundErrors.includes('password') && newPassword !== confirmNewPassword) foundErrors.push('confirmNewPassword');
  setErrors(foundErrors);
  return foundErrors.length === 0;
}

const handleSubmitEmailToResetPassword = async () => {
    try {
      if (!isFormValid()) {
        setErrorMessage('Please enter a valid email')
        return;
      }
      setIsLoading(true)
      let username = email.toLowerCase();
      await beginForgottenPasswordProcess(username);
      setHasSentCode(true);
      setErrorMessage(undefined);
    } catch (e) {
      console.error('LOGIN ERROR', e)
      setErrorMessage(e)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangePassword = async () => {
    try {
      if (isPasswordFormValid().length > 0) {
        setErrorMessage('Please enter a valid email')
        return;
      }
      setIsLoading(true)
      let username = email.toLowerCase();
      await resetForgottenPassword(username, code, newPassword);
      setHasSentCode(true);
      setErrorMessage(undefined);
      showNotificationBar('success', 'Successfully updated your password! Please try to login now.')
      history.push('/login')
    } catch (e) {
      console.error('LOGIN ERROR', e)
      setErrorMessage('Uh oh! Something went wrong, please double check your verification code.')
    } finally {
      setIsLoading(false)
    }
  }

  const renderForm = () => (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <h3 style={{ textAlign: 'center', margin: 0, fontSize: '28px' }}>Find Your Account</h3>
        <p>
          Enter the email associated with your account.
         </p>
        {errorMessage && (
          <p sx={{ color: Colors.redColor, fontSize: '0.875rem', mt: 1 }}>
            {errorMessage}
          </p>
        )}
      </Box>
  
      <StyledTextField
        fullWidth
        autoFocus
        key="email"
        value={email}
        label="Email"
        onKeyPress={(e) => {
          if (e.key === 'Enter') handleSubmitEmailToResetPassword();
        }}
        onChange={(e) => setEmail(e.target.value)}
        sx={{ mb: 3 }}
      />
  
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <TextButton onClick={goToSignIn} disabled={hasResentCode}>
          Cancel
        </TextButton>
        <LoadingButton
          onClick={handleSubmitEmailToResetPassword}
          disabled={isLoading}
          isLoading={isLoading}
        >
          Recover Account
        </LoadingButton>
      </Box>
    </Box>
  );
  
  const renderResetPasswordForm = () => (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
      <h3 style={{ textAlign: 'center', margin: 0, fontSize: '28px' }}>Find Your Account</h3>
        <p>
          We emailed you a code. Provide it below along with your new password.
        </p>
        {errorMessage && (
          <p sx={{ color: Colors.redColor, fontSize: '0.875rem', mt: 1 }}>
            {errorMessage}
          </p>
        )}
      </Box>
  
      <Box>
        <StyledTextField
          fullWidth
          key="code"
          value={code}
          error={errors.includes('verificationCode')}
          helperText={errors.includes('verificationCode') && 'This is not valid. The code sent should have been 6 digits.'}
          label="Verification Code"
          onChange={(e) => setCode(e.target.value)}
          sx={{ mb: 2 }}
        />
        <StyledTextField
          fullWidth
          value={newPassword}
          type="password"
          error={errors.includes('newPassword')}
          helperText={errors.includes('newPassword') && 'A password must be 8 characters containing a capital letter and a number.'}
          label="New Password"
          onChange={(e) => setNewPassword(e.target.value)}
          sx={{ mb: 2 }}
        />
        <StyledTextField
          fullWidth
          value={confirmNewPassword}
          type="password"
          error={errors.includes('confirmNewPassword')}
          helperText={errors.includes('confirmNewPassword') && 'The passwords did not match.'}
          label="Confirm New Password"
          onChange={(e) => setConfirmNewPassword(e.target.value)}
          sx={{ mb: 3 }}
        />
      </Box>
  
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <TextButton onClick={handleResendCode} disabled={isLoading || hasResentCode}>
          Resend Code
        </TextButton>
        <LoadingButton onClick={handleChangePassword} disabled={isLoading} isLoading={isLoading}>
          Reset Password
        </LoadingButton>
      </Box>
    </Box>
  );
  
  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: '600px',
          display: 'flex',               // ADD THIS
          justifyContent: 'center',     // Center horizontally
          alignItems: 'center',         // Center vertically
          padding: '32px',
          boxSizing: 'border-box',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ width: '100%' }}>
          {hasSentCode ? renderResetPasswordForm() : renderForm()}
        </Box>
      </Paper>
    </Box>
  );
  
  
}

export default ForgotPasswordForm;
